import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  initAnalytics,
  trackCtaClick,
  trackPageView,
  trackSectionView,
} from "@/lib/analytics";

const PRIVATE_PREFIXES = ["/crm", "/portal"];

const isPrivate = (pathname: string) =>
  PRIVATE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/**
 * Spina podstawowe zdarzenia analityczne:
 * - page_view przy każdej zmianie trasy,
 * - cta_click (delegowany listener na linkach/przyciskach CTA),
 * - section_view przy przewinięciu do sekcji z id.
 */
const Analytics = () => {
  const { pathname } = useLocation();
  const seenSections = useRef<Set<string>>(new Set());

  useEffect(() => {
    initAnalytics();
  }, []);

  // page_view przy zmianie trasy
  useEffect(() => {
    if (isPrivate(pathname)) return;
    const id = window.setTimeout(() => trackPageView(pathname), 50);
    return () => window.clearTimeout(id);
  }, [pathname]);

  // cta_click - delegowany listener
  useEffect(() => {
    if (isPrivate(pathname)) return;

    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const el = target?.closest<HTMLElement>("[data-cta], a[href], button");
      if (!el) return;

      const explicit = el.dataset.cta;
      const href = el.getAttribute("href") ?? undefined;

      // Bez data-cta śledzimy tylko linki (kotwice, mailto, tel, zewnętrzne).
      if (!explicit && !href) return;

      const label =
        explicit ||
        el.getAttribute("aria-label") ||
        el.textContent?.trim().slice(0, 80) ||
        "unknown";

      trackCtaClick(label, href, pathname);
    };

    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, [pathname]);

  // section_view - pierwsze wejście sekcji w viewport
  useEffect(() => {
    if (isPrivate(pathname)) return;
    seenSections.current = new Set();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.id;
          if (!entry.isIntersecting || !id || seenSections.current.has(id)) continue;
          seenSections.current.add(id);
          trackSectionView(id);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.35 }
    );

    // Sekcje montują się leniwie, więc obserwujemy też późniejsze wstawki.
    const attach = () => {
      document.querySelectorAll<HTMLElement>("section[id], [data-section-id]").forEach((el) => {
        if (!seenSections.current.has(el.id)) observer.observe(el);
      });
    };
    attach();
    const mo = new MutationObserver(attach);
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      observer.disconnect();
    };
  }, [pathname]);

  return null;
};

export default Analytics;
